import { TruckIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import React from 'react';
import { FulfilmentOptionsProps } from '../utils/types';
import DeliveryChoice from '@jbhi-fi/lanyard-ui/components/DeliveryChoice/DeliveryChoice';
import LocationSearch from '../../location-search/LocationSearch';
import { styles } from '../styles/Availability.css';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner/Spinner';
import { DeliveryLocation } from '../../localisation/utils/types';
import { CHECK_ALL_DELIVERY_MESSAGE } from '../utils/constants';
import { availabilityVariantType } from '../availability-wrapper/AvailabilityWrapper';
import { formattedUberDeliveryOption } from '../utils/availabilityUtils';
import { premiumDeliveryOptions as premiumDeliveryOptionsWithContent } from '@jbhi-fi/utils/src/premiumDelivery/premiumDelivery';
import { FulfilmentOptionProps } from '../../fulfilment/fulfilment-option/FulfilmentOption';
import { premiumDeliveryOptionValues } from '@jbhi-fi/utils/src/premiumDelivery/premiumDelivery';

export type DeliveryAvailabilityProps = {
  deliveryLocation: DeliveryLocation;
  isLoading: boolean;
  deliveryOptions: FulfilmentOptionsProps;
  error?: string;
  variant?: availabilityVariantType;
  isErrorMessage?: string;
  productGroup?: string;
  handleDeliveryLocationUpdated: (location: DeliveryLocation) => void;
};

const DeliveryChoiceComponent = ({ option, isPremium }: { option: FulfilmentOptionProps; isPremium: boolean }) => {
  return (
    <DeliveryChoice
      experienceName={isPremium ? '' : option.experienceName}
      deliveryDate={(isPremium ? option.experienceName : option.title) ?? ''}
      price={option.price}
      carrier={{
        name: option.serviceName,
        imageUrl: option.serviceImage,
      }}
      tags={isPremium ? [] : option.tags ?? []}
      shouldShowGreenCircle={!isPremium}
    />
  );
};

const DeliveryAvailability = ({
  deliveryLocation,
  isLoading,
  deliveryOptions,
  error,
  variant,
  isErrorMessage = '',
  handleDeliveryLocationUpdated,
}: DeliveryAvailabilityProps) => {
  const testidPrefix = 'delivery-availability-';
  const nonPremiumDeliveryOptions: Array<FulfilmentOptionProps> = [];
  const premiumDeliveryOptions: Array<FulfilmentOptionProps> = [];

  deliveryOptions?.options?.forEach(option => {
    if (premiumDeliveryOptionValues.includes(option.experienceName ?? '')) {
      premiumDeliveryOptions.push(option);
    } else {
      nonPremiumDeliveryOptions.push(option);
    }
  });

  return (
    <div className={styles.availabilityContainer} data-testid={`${testidPrefix}container`}>
      <div className={styles.searchLocationContainer} data-testid={`${testidPrefix}location-container`}>
        <div className={styles.searchLocationHeader} data-testid={`${testidPrefix}location-title`}>
          {CHECK_ALL_DELIVERY_MESSAGE}
        </div>
        <LocationSearch
          id="delivery-availability"
          initialValue={deliveryLocation?.displayName}
          showUseMyLocationButton={false}
          gtmEventSource="delivery-availability"
          onLocationSelect={handleDeliveryLocationUpdated}
        />
      </div>
      <div className={styles.availabilityHeader({ variant: variant })} data-testid={`${testidPrefix}header`}>
        <div className={styles.headerIcon} data-testid={`${testidPrefix}icon-wrapper`}>
          <TruckIllustrated width="56.66px" height="39.05px" />
        </div>
        <div className={styles.headerContent} data-testid={`${testidPrefix}header-content`}>
          <div className={styles.heading({ variant: variant })} data-testid={`${testidPrefix}container`}>
            Your delivery location
          </div>
          <div data-testid={`${testidPrefix}header-info`}>
            {deliveryLocation?.suburb} {deliveryLocation?.postcode}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className={styles.loader}>
          <Spinner />
        </div>
      ) : (
        <>
          {isErrorMessage ? (
            <div className={styles.noResultsContainer} data-testid={`${testidPrefix}location-search-error`}>
              {isErrorMessage}
            </div>
          ) : error ? (
            <div className={styles.noResultsContainer} data-testid={`${testidPrefix}location-search-no-results`}>
              {error}
            </div>
          ) : (
            <>
              <div style={{ marginTop: '1.25rem' }} data-testid={`${testidPrefix}location-search-results`}>
                {nonPremiumDeliveryOptions?.map((option, i) => {
                  const isUberDeliveryOption = option.serviceImage?.includes('uber');
                  const isSingleOption = nonPremiumDeliveryOptions.length === 1;
                  if (isUberDeliveryOption) {
                    option.title = formattedUberDeliveryOption(option.title ?? '');
                  }

                  return (
                    <div
                      className={styles.deliveryChoiceRow({ isSingleOption })}
                      key={`${option.experienceName}-${i}`}
                      data-testid={`${testidPrefix}location-search-result`}
                    >
                      <DeliveryChoiceComponent option={option} isPremium={false} />
                    </div>
                  );
                })}
              </div>
              {premiumDeliveryOptions?.length ? (
                <div className={styles.addOnsContainer}>
                  <span className={styles.addOnsHeading}>Available delivery add-ons</span>
                  {premiumDeliveryOptions?.map((option, i) => (
                    <div className={styles.addOnsDeliveryChoiceRow} key={`${option.experienceName}-${i}`}>
                      <DeliveryChoiceComponent option={option} isPremium={true} />
                      <div className={styles.addOnsFeatureContainer}>
                        {premiumDeliveryOptionsWithContent
                          .filter(opt => opt.title.toLowerCase() === option.experienceName?.toLowerCase())
                          .flatMap(opt => opt.description)
                          .map((description, i) => (
                            <li key={i} className={styles.addOnsFeatureListItem}>
                              {description.feature}
                            </li>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAvailability;
